import { useMemo } from "react";
import { useGetLocale, useTranslate } from "@refinedev/core";
import { CheckCircle2, CreditCard, Flag, ShieldCheck } from "lucide-react";

import { formatEuro, localDateIso } from "../i18n/format";
import type { DossierStap } from "./CrematieDossierStappen";
import "./LogboekTile.css";

// Het logboek van een uitvaart, in de vorm van het logboek bij het dier: per dag een kop, per regel
// de tijd, een gekleurde tag die zegt wat voor gebeurtenis het was, wat er gebeurde en door wie.
//
// Het dossier houdt zijn geschiedenis op vier plekken bij — statuswissels, identificatiecontroles,
// afgeronde handelingen en betalingen. Vier losse tabelletjes onder elkaar leest niemand als één
// verhaal; één tijdlijn wel. De regels worden hier samengevoegd en op tijd gezet, nieuwste eerst.

export interface LogStatus {
  id: string;
  naarStatus: string | null;
  tijdstip: string | null;
  gebruiker: string | null;
  opmerking: string | null;
}
export interface LogControle {
  id: string;
  stap: string | null;
  uitkomst: string | null;
  gecontroleerdDoor: string | null;
  tijdstip: string | null;
  opmerking: string | null;
}
export interface LogBetaling {
  id: string;
  betaaldOp: string | null;
  betaalwijze: string | null;
  bedrag: number | null;
  geregistreerdDoor: string | null;
}

type Soort = "STATUS" | "CONTROLE_OK" | "CONTROLE_AF" | "HANDELING" | "BETALING";

interface Regel {
  id: string;
  soort: Soort;
  label: string;
  tekst: string;
  detail: string | null;
  door: string | null;
  op: string;
}

export function CrematieDossierLogboek({
  statuslog,
  controles,
  betalingen,
  stappen,
}: {
  statuslog: LogStatus[];
  controles: LogControle[];
  betalingen: LogBetaling[];
  stappen: DossierStap[];
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const regels = useMemo<Regel[]>(() => {
    const uit: Regel[] = [];
    for (const s of statuslog) {
      if (!s.tijdstip) continue;
      uit.push({
        id: `s-${s.id}`,
        soort: "STATUS",
        label: t("crematieDetail.log.status"),
        tekst: s.naarStatus ? t(`crematies.status.${s.naarStatus}`) : "—",
        detail: s.opmerking,
        door: s.gebruiker,
        op: s.tijdstip,
      });
    }
    for (const c of controles) {
      if (!c.tijdstip) continue;
      const akkoord = c.uitkomst === "AKKOORD";
      uit.push({
        id: `c-${c.id}`,
        soort: akkoord ? "CONTROLE_OK" : "CONTROLE_AF",
        label: t("crematieDetail.log.controle"),
        // De stap is een code (AANMELDING_DEFINITIEF); leesbaar gemaakt, niet vertaald — er is geen lijst.
        tekst: [c.stap ? leesbaar(c.stap) : null,
          c.uitkomst ? t(`crematieDetail.uitkomst.${c.uitkomst}`) : null].filter(Boolean).join(" · "),
        detail: c.opmerking,
        door: c.gecontroleerdDoor,
        op: c.tijdstip,
      });
    }
    for (const h of stappen) {
      if (!h.uitgevoerdOp) continue;
      uit.push({
        id: `h-${h.handelingId}`,
        soort: "HANDELING",
        label: t("crematieDetail.log.handeling"),
        tekst: h.handeling,
        detail: h.opmerking,
        door: h.uitgevoerdDoor,
        op: h.uitgevoerdOp,
      });
    }
    for (const b of betalingen) {
      if (!b.betaaldOp) continue;
      uit.push({
        id: `b-${b.id}`,
        soort: "BETALING",
        label: t("crematieDetail.log.betaling"),
        tekst: [b.bedrag != null ? formatEuro(b.bedrag, locale) : null, b.betaalwijze].filter(Boolean).join(" · "),
        detail: null,
        door: b.geregistreerdDoor,
        op: b.betaaldOp,
      });
    }
    // Nieuwste bovenaan: wat net gebeurde is wat je zoekt.
    uit.sort((a, b) => b.op.localeCompare(a.op));
    return uit;
  }, [statuslog, controles, betalingen, stappen, t, locale]);

  const groepen = useMemo(() => groepeerPerDag(regels, locale, t), [regels, locale, t]);

  return (
    <div className="logboek">
      <div className="logboek__lijst">
        {regels.length === 0 && <p className="logboek__leeg">{t("crematieDetail.historie.empty")}</p>}
        {groepen.map((groep) => (
          <div key={groep.sleutel}>
            <h4 className="logboek__dag">{groep.kop}</h4>
            {groep.regels.map((regel) => (
              <div key={regel.id} className="logboek__regel">
                <span className="logboek__tijd">{tijd(regel.op, locale)}</span>
                <span className="logboek__stip" aria-hidden="true" />
                <span className={`logboek__chip logboek__chip--${regel.soort}`}>
                  <SoortIcoon soort={regel.soort} />
                  {regel.label}
                </span>
                <div className="logboek__tekst">
                  <p className="logboek__wat">{regel.tekst}</p>
                  <p className="logboek__door">
                    {regel.door ? t("logboek.door", { args: [regel.door] }) : t("logboek.doorOnbekend")}
                    {regel.detail && <span className="logboek__bron"> · {regel.detail}</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function SoortIcoon({ soort }: { soort: Soort }) {
  const p = { size: 14, "aria-hidden": true as const };
  switch (soort) {
    case "STATUS": return <Flag {...p} />;
    case "CONTROLE_OK":
    case "CONTROLE_AF": return <ShieldCheck {...p} />;
    case "HANDELING": return <CheckCircle2 {...p} />;
    case "BETALING": return <CreditCard {...p} />;
  }
}

// ── per dag, zoals het logboek bij het dier ──────────────────────────────────────────────────────

interface DagGroep {
  sleutel: string;
  kop: string;
  regels: Regel[];
}

function groepeerPerDag(regels: Regel[], locale: string, t: (k: string, o?: unknown) => string): DagGroep[] {
  const vandaag = localDateIso(new Date());
  const gisteren = localDateIso(new Date(Date.now() - 864e5));
  const groepen: DagGroep[] = [];
  for (const regel of regels) {
    const datum = new Date(regel.op);
    const sleutel = Number.isNaN(datum.getTime()) ? regel.op : localDateIso(datum);
    let groep = groepen.find((g) => g.sleutel === sleutel);
    if (!groep) {
      const kop = sleutel === vandaag ? t("logboek.vandaag")
        : sleutel === gisteren ? t("logboek.gisteren")
        : Number.isNaN(datum.getTime()) ? sleutel
        : datum.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });
      groep = { sleutel, kop, regels: [] };
      groepen.push(groep);
    }
    groep.regels.push(regel);
  }
  return groepen;
}

function tijd(iso: string, locale: string): string {
  const datum = new Date(iso);
  if (Number.isNaN(datum.getTime())) return "";
  return datum.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

/** AANMELDING_DEFINITIEF → "Aanmelding definitief". */
function leesbaar(code: string): string {
  const woorden = code.toLowerCase().replace(/_/g, " ");
  return woorden.charAt(0).toUpperCase() + woorden.slice(1);
}
